import React, { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, Timestamp, where } from "firebase/firestore";
import { auth, db, storage } from "../firebase";
import { C } from "../theme";
import useGoPro from "../hooks/useGoPro";
import useVoiceCommands from "../hooks/useVoiceCommands";
import VoiceWave from "../components/VoiceWave";
import CollapsibleSection from "../components/CollapsibleSection";
import { speak } from "../services/tts";
import { playRecordLimitWarningCue } from "../utils/audioCues";
import Badge from "../components/Badge";
import PulseDot from "../components/PulseDot";
import { useSessionContext } from "../context/SessionContext";
import { sha256Hex, formatTimestamp } from "../utils";
import { authedWrite } from "../utils/authedWrite";
import { sendDirectorCertifiedVideoEvent } from "../services/tournamentEventWriters";
import { isR2Backend } from "../config/videoBackend";
import { uploadVideo as r2UploadVideo, getPlaybackUrl as r2GetPlaybackUrl } from "../services/r2Video";

// Open GoPro video-resolution / FPS enum codes (16:9). Model-specific — verified
// for the HERO 13. See useGoPro SETTING_ID_RESOLUTION / SETTING_ID_FPS.
const RESOLUTION_OPTIONS = [
  { label: "5.3K", value: 100 },
  { label: "4K", value: 1 },
  { label: "2.7K", value: 4 },
  { label: "1080", value: 9 },
];
const FPS_OPTIONS = [
  { label: "24", value: 10 },
  { label: "30", value: 8 },
  { label: "60", value: 5 },
  { label: "120", value: 1 },
  { label: "240", value: 0 },
];
// Utility: find best-matching session event for a GoPro clip
async function findMatchingSessionEvent({ userId, cameraName, creationTime, durationSec }) {
  if (!userId || !creationTime) return null;
  // Query recent sessions for this user
  const sessionsQ = query(collection(db, "users", userId, "sessions"), where("startedAt", ">=", Timestamp.fromDate(new Date(Date.now() - 1000 * 60 * 60 * 24 * 2))));
  const sessionsSnap = await getDocs(sessionsQ);
  let best = null, bestDrift = null;
  for (const sessionDoc of sessionsSnap.docs) {
    const session = sessionDoc.data();
    if (!session?.startedAt) continue;
    const sessionStart = session.startedAt.toDate ? session.startedAt.toDate() : new Date(session.startedAt);
    // Assume cameraName is not always available; match by time only
    const drift = Math.abs(sessionStart.getTime() - creationTime.getTime());
    if (drift <= 300000 && (bestDrift == null || drift < bestDrift)) {
      best = { sessionId: sessionDoc.id, sessionStart, driftMs: drift };
      bestDrift = drift;
    }
  }
  return best;
}
// Eligibility badge component
function EligibilityBadge({ eligible, reason, driftMs }) {
  // While joined to the GoPro Wi-Fi the app can't reach the server to verify,
  // so suppress the transient "Checking" pill rather than showing it on every row.
  if (reason && reason.startsWith("Checking")) return null;
  if (eligible) return <Badge color={C.seafoam}>Eligible</Badge>;
  if (driftMs != null && driftMs <= 60000) return <Badge color={C.gold}>Unverified</Badge>;
  return <Badge color={C.red}>{reason || "Ineligible"}</Badge>;
}

// Clip row with lazy thumbnail + certify-eligibility logic.
// NOTE: This MUST live at module scope. When it was declared inside the parent
// screen's render body, every parent re-render (GoPro pushes a status packet
// ~1x/sec) created a new component identity, so React remounted every row —
// which re-fetched thumbnails and re-ran the eligibility check every second
// (visible flicker / "refreshing every second").
// GoPro's HTTP server serializes poorly and stalls under concurrent requests, so
// clip-thumbnail fetches run one at a time app-wide (and are paused during a
// download) to keep transfers from hanging.
let thumbFetchChain = Promise.resolve();
function queueThumbFetch(task) {
  const run = thumbFetchChain.then(task, task);
  thumbFetchChain = run.then(() => {}, () => {});
  return run;
}
function ClipRowWithEligibility({ clip, isUploaded, catchParts, busy, handleDownload, handleDelete, fmtBytes, userId, getThumbnail, wifiBound, thumbsEnabled = true }) {
  const [eligibility, setEligibility] = React.useState({ eligible: false, reason: "Checking…", driftMs: null });
  const [thumb, setThumb] = React.useState(null);
  React.useEffect(() => {
    // Need the GoPro AP bound to reach the thumbnail endpoint; skip while a
    // download is active so these requests don't compete with (and stall) it.
    if (!wifiBound || !thumbsEnabled || thumb) return undefined;
    let cancelled = false;
    queueThumbFetch(async () => {
      if (cancelled) return;
      try {
        const src = await getThumbnail?.(clip);
        if (!cancelled && src) setThumb(src);
      } catch (_) { /* keep placeholder; retries when re-enabled */ }
    });
    return () => { cancelled = true; };
  }, [clip.folder, clip.name, getThumbnail, wifiBound, thumbsEnabled, thumb]);
  React.useEffect(() => {
    let cancelled = false;
    async function check() {
      // While joined to the GoPro Wi-Fi AP the phone has no route to Firebase,
      // so a session lookup can't succeed. Defer the check until after upload
      // (the app releases the AP then).
      if (wifiBound) {
        setEligibility({ eligible: false, reason: "Checking after upload", driftMs: null });
        return;
      }
      if (!clip.mp4CreationTime) {
        setEligibility({ eligible: false, reason: "No creation time", driftMs: null });
        return;
      }
      const match = await findMatchingSessionEvent({
        userId,
        cameraName: clip.folder,
        creationTime: clip.mp4CreationTime,
        durationSec: clip.mp4DurationSec,
      });
      if (cancelled) return;
      // Loosened windows: clocks between the GoPro and the app are often a
      // minute or two apart, so 30s was too strict.
      if (match && match.driftMs <= 120000) {
        setEligibility({ eligible: true, reason: null, driftMs: match.driftMs });
      } else if (match && match.driftMs <= 300000) {
        setEligibility({ eligible: false, reason: "Unverified (drift)", driftMs: match.driftMs });
      } else {
        setEligibility({ eligible: false, reason: "No matching session", driftMs: null });
      }
    }
    check();
    return () => { cancelled = true; };
  }, [clip.mp4CreationTime, clip.mp4DurationSec, userId, clip.folder, wifiBound]);
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 8, marginBottom: 8 }}>
      <button
        onClick={() => handleDownload(clip)}
        disabled={busy}
        style={{
          flex: 1, minWidth: 0, display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "transparent",
          border: `1px solid ${isUploaded ? C.seafoam + "66" : C.border}`,
          color: isUploaded ? C.muted : C.white,
          borderRadius: 10, padding: "10px 12px",
          cursor: "pointer", textAlign: "left",
          opacity: isUploaded ? 0.7 : 1,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <span
            style={{
              width: 64, height: 40, flexShrink: 0,
              borderRadius: 6, overflow: "hidden",
              background: C.midnight,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `1px solid ${C.border}`,
            }}
          >
            {thumb ? (
              <img
                src={thumb}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              <span style={{ fontSize: 16, opacity: 0.5 }}>🎬</span>
            )}
          </span>
          {isUploaded && (
            <span
              title="Already uploaded"
              aria-label="Already uploaded"
              style={{
                fontSize: 11, lineHeight: 1,
                color: C.seafoam,
                border: `1px solid ${C.seafoam}`,
                borderRadius: 999,
                padding: "2px 6px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              ✓
            </span>
          )}
          <span style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, textDecoration: isUploaded ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {clip.createdAt
                ? new Date(clip.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                : "Video clip"}
            </div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
              {fmtBytes(clip.size)}{isUploaded ? " · uploaded" : ""}
            </div>
            {catchParts.length > 0 && (
              <div style={{ fontSize: 11, color: C.seafoam, marginTop: 4, fontWeight: 600 }}>
                {catchParts.join(" · ")}
              </div>
            )}
          </span>
        </span>
        <span style={{ marginLeft: 8, flexShrink: 0 }}>
          <EligibilityBadge eligible={eligibility.eligible} reason={eligibility.reason} driftMs={eligibility.driftMs} />
        </span>
      </button>
      <button
        onClick={() => handleDelete(clip)}
        disabled={busy}
        title="Delete from GoPro"
        aria-label="Delete from GoPro"
        style={{
          flexShrink: 0,
          background: "transparent",
          border: `1px solid ${C.red}66`,
          color: C.red,
          borderRadius: 10,
          padding: "0 14px",
          fontSize: 16,
          cursor: busy ? "not-allowed" : "pointer",
        }}
      >
        🗑
      </button>
    </div>
  );
}

const genCertId = () => {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  return `T99-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
};

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function GoProScreen({ onBack, onLogCatch, onLogCatchFirst, pendingCatch, onClearPendingCatch, onCertify, userProfile, location, tournamentGate, maxClipMins = 5, clipCatchOverride = null }) {
  const gp = useGoPro();
  const { sessionId } = useSessionContext();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  // After Stop Recording, offer to log the catch now so it can be attached to
  // the clip at certify time (skipping the post-upload Log Catch step).
  const [showLogCatchPrompt, setShowLogCatchPrompt] = useState(false);
  // Clip auto-stop timer: warn 30s before the max, then hard-stop at the cap.
  const [limitWarnOpen, setLimitWarnOpen] = useState(false);
  const [limitRemaining, setLimitRemaining] = useState(null); // sec until auto-stop
  const recordStartRef = useRef(null);
  const limitWarnedRef = useRef(false);
  const limitStoppedRef = useRef(false);
  const toggleRecordRef = useRef(null);
  const [clipList, setClipList] = useState([]);
  const [downloaded, setDownloaded] = useState(null); // { path, uri, size, name }
  // True only while a clip transfer is actively running, so the progress card
  // can't linger as a phantom after the download completes or hands off.
  const [downloadingClip, setDownloadingClip] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadDone, setUploadDone] = useState(null); // download URL
  const [uploadCertId, setUploadCertId] = useState(null);
  const [statusOpen, setStatusOpen] = useState(true);
  // Camera video-settings section (collapsible). Optimistic local selection so
  // the resolution/FPS buttons highlight instantly on tap; the camera's
  // confirmed value arrives async via gp.status.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selRes, setSelRes] = useState(null);
  const [selFps, setSelFps] = useState(null);
  // Clip pending user confirmation for deletion (styled modal). null = closed.
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Track clip keys ("folder/name") that have been successfully uploaded so they
  // disappear from the available-for-upload list. Persisted to localStorage so the
  // list stays clean across app restarts. Re-upload of a previously uploaded clip
  // will be a separate flow (not yet implemented).
  const UPLOADED_KEY = "the99.gopro.uploadedClips.v1";
  const [uploadedClips, setUploadedClips] = useState(() => {
    try {
      const raw = localStorage.getItem(UPLOADED_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch (_) { return new Set(); }
  });
  const markClipUploaded = (key) => {
    setUploadedClips((prev) => {
      const next = new Set(prev);
      next.add(key);
      try { localStorage.setItem(UPLOADED_KEY, JSON.stringify([...next])); } catch (_) {}
      return next;
    });
  };
  // Cached catch metadata for previously-uploaded clips, keyed by "folder/name".
  // Resolved by looking up the matching videos/{certId} doc by originalName,
  // then following catchRef to the catch doc. Persisted to localStorage so we
  // don't re-query Firestore every time the user opens the screen.
  const CATCH_INFO_KEY = "the99.gopro.clipCatchInfo.v1";
  const [clipCatchInfo, setClipCatchInfo] = useState(() => {
    try {
      const raw = localStorage.getItem(CATCH_INFO_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });
  const persistClipCatchInfo = (next) => {
    try { localStorage.setItem(CATCH_INFO_KEY, JSON.stringify(next)); } catch (_) {}
  };
  // Fire-and-forget: for each uploaded clip in `clips` that doesn't already have
  // catch info cached, look it up in Firestore.
  const enrichClipsWithCatch = async (clips) => {
    const uid = auth.currentUser?.uid;
    if (!uid || !Array.isArray(clips) || clips.length === 0) return;
    const targets = clips.filter((c) => {
      const k = `${c.folder}/${c.name}`;
      return !clipCatchInfo[k]?.species;
    });
    if (targets.length === 0) return;
    const additions = {};
    await Promise.all(targets.map(async (clip) => {
      const key = `${clip.folder}/${clip.name}`;
      try {
        const queryVideosByField = async (field, value) => {
          const snap = await getDocs(query(collection(db, "videos"), where(field, "==", value), limit(8)));
          return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        };
        // Match by original camera name first (covers trimmed uploads whose
        // originalName was changed), then fall back to originalName.
        let allDocs = await queryVideosByField("goproClipName", clip.name);
        let match = allDocs.find((v) => v.userId === uid);
        if (!match) {
          allDocs = await queryVideosByField("originalName", clip.name);
          match = allDocs.find((v) => v.userId === uid);
        }
        if (!match) return;
        // Prefer denormalized catch fields written by CatchScreen.backfillCert.
        let species = match.species ?? null;
        let lengthIn = typeof match.lengthIn === "number" ? match.lengthIn : null;
        let weightLbs = typeof match.weightLbs === "number" ? match.weightLbs : null;
        let lure = match.lure ?? null;
        // Fallback: follow catchRef to the catches doc if denormalized fields
        // aren't present (older uploads written before denorm shipped).
        if (!species && match.catchRef) {
          const cr = match.catchRef;
          if (cr.userId && cr.sessionId && cr.catchId) {
            try {
              const cSnap = await getDoc(doc(db, "users", cr.userId, "sessions", cr.sessionId, "catches", cr.catchId));
              if (cSnap.exists()) {
                const data = cSnap.data() || {};
                species = data.species ?? species;
                lengthIn = typeof data.lengthIn === "number" ? data.lengthIn : lengthIn;
                weightLbs = typeof data.weightLbs === "number" ? data.weightLbs : weightLbs;
                lure = data.lure ?? lure;
              }
            } catch (_) {}
          }
        }
        if (!species && lengthIn == null && weightLbs == null) return;
        // Mark this clip as uploaded so the row gets the green check + line-through
        // styling, even if we missed the upload event in this session.
        if (!uploadedClips.has(key)) markClipUploaded(key);
        additions[key] = {
          certId: match.id,
          species,
          lengthIn,
          weightLbs,
          lure,
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[GoPro] catch lookup failed for", key, e?.message || e);
      }
    }));
    if (Object.keys(additions).length === 0) return;
    setClipCatchInfo((prev) => {
      const next = { ...prev, ...additions };
      persistClipCatchInfo(next);
      return next;
    });
  };
  const isNative = Capacitor.isNativePlatform();

  // Auto-poll status while connected
  useEffect(() => {
    if (!gp.connected) return;
    let stop = false;
    const tick = async () => {
      try { await gp.refreshStatus(); } catch (_) {}
      if (!stop) setTimeout(tick, 5000);
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gp.connected, gp.refreshStatus]);

  // Read the camera's current resolution / FPS once connected so the pickers
  // highlight the active values.
  useEffect(() => {
    if (gp.connected?.deviceId) {
      gp.refreshSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gp.connected?.deviceId]);

  // Mirror the camera's confirmed resolution/FPS into the local selection.
  useEffect(() => { if (gp.status.resolution != null) setSelRes(gp.status.resolution); }, [gp.status.resolution]);
  useEffect(() => { if (gp.status.fps != null) setSelFps(gp.status.fps); }, [gp.status.fps]);

  // Whenever the clip list or the set of uploaded clips changes, look up any
  // missing catch metadata so previously-uploaded clips show species / length
  // / weight inline in the list.
  useEffect(() => {
    if (clipList.length > 0) {
      enrichClipsWithCatch(clipList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipList]);

  // When the certify/catch flow completes, the host hands back the just-logged
  // catch keyed by clip. Stamp it directly onto the list (no Firestore round-trip
  // — works even while rejoined to the internet-less GoPro AP) and mark uploaded.
  useEffect(() => {
    const o = clipCatchOverride;
    if (!o?.clipKey) return;
    const display = [o.species, o.size, o.weight].filter((v) => v && v !== "—");
    setClipCatchInfo((prev) => {
      const existing = prev[o.clipKey];
      // Don't clobber a richer Firestore entry that already has numeric fields.
      if (existing?.species && existing?.lengthIn != null) return prev;
      const next = {
        ...prev,
        [o.clipKey]: { certId: o.certId || existing?.certId || null, species: o.species, size: o.size, weight: o.weight, lure: o.lure, display },
      };
      persistClipCatchInfo(next);
      return next;
    });
    try { markClipUploaded(o.clipKey); } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipCatchOverride]);

  const wrap = (fn) => async (...args) => {
    setActionError(null);
    setBusy(true);
    try { await fn(...args); } catch (e) { setActionError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  // Tournament upload window: when the angler is in an active tournament,
  // GoPro clips may only be uploaded/certified during the tournament's
  // start–end window. Returns a human reason string when blocked, else null.
  const tournamentUploadBlockReason = (nowMs = Date.now()) => {
    const gate = tournamentGate;
    if (!gate || !gate.inTournament) return null;
    // A cancelled/ended/archived tournament is over — uploads resume as
    // recreational (they simply won't count toward the tournament).
    if (gate.terminal) return null;
    const label = gate.name || "tournament";
    if (gate.startMs && nowMs < gate.startMs) {
      return `The ${label} hasn't started yet — GoPro videos can only be uploaded during the tournament window.`;
    }
    if (gate.endMs && nowMs > gate.endMs) {
      return `The ${label} has ended — GoPro videos can only be uploaded during the tournament window.`;
    }
    return null;
  };
  const assertTournamentUploadAllowed = () => {
    const reason = tournamentUploadBlockReason();
    if (reason) throw new Error(reason);
  };

  const handleScan = wrap(() => gp.scan({ timeoutMs: 10000 }));
  const [connectingId, setConnectingId] = useState(null);
  const handleConnect = wrap(async (d) => {
    setConnectingId(d.deviceId);
    try { await gp.connect(d); } finally { setConnectingId(null); }
  });
  const handleDisconnect = wrap(() => gp.disconnect());
  const handleToggleRecord = wrap(async () => {
    if (gp.recording) {
      await gp.stopRecording();
      // Offer to log the catch for the clip we just recorded. Only ask when a
      // recording session is active (the catch needs a sessionId to save) and
      // no catch is already staged for the next clip.
      if (sessionId && !pendingCatch) setShowLogCatchPrompt(true);
      // GoPro needs ~1-3s after stop to finalize the MP4 and add it to the
      // media list. Auto-join the camera Wi-Fi (if not already) so the user
      // doesn't have to tap "Join GoPro Wi-Fi" before refreshing the clip list.
      if (!gp.wifi.connected) {
        try {
          setWifiJoining(true);
          setWifiPhase("creds");
          try { await gp.readWifiCredentials?.(); } catch (_) {}
          setWifiPhase("ap");
          try { await gp.enableAp?.(); } catch (_) {}
          setWifiPhase("join");
          await gp.connectWifi();
        } catch (_) {
          // Best-effort. User can still tap "Join GoPro Wi-Fi" manually.
        } finally {
          setWifiJoining(false);
          setWifiPhase(null);
        }
      }
      const tryRefresh = async (delayMs) => {
        await new Promise((r) => setTimeout(r, delayMs));
        try {
          const list = await gp.listMedia();
          setClipList(list);
          enrichClipsWithCatch(list);
          return list.length;
        } catch (_) { return -1; }
      };
      const before = clipList.length;
      const after1 = await tryRefresh(2000);
      // If the file didn't show up yet, try once more after another 2s.
      if (after1 >= 0 && after1 <= before) {
        await tryRefresh(2000);
      }
    } else {
      await gp.startRecording();
    }
  });

  // Keep a stable ref to the latest toggle handler so the interval below can
  // trigger the auto-stop without re-subscribing every render.
  useEffect(() => {
    toggleRecordRef.current = handleToggleRecord;
  });

  // Clip-limit watchdog: while the GoPro is recording, warn the user 30s before
  // the configured cap (audible + on-screen), then auto-stop at the cap.
  const WARN_LEAD_SEC = 30;
  const maxClipSec = Math.min(10, Math.max(1, Number(maxClipMins) || 5)) * 60;
  useEffect(() => {
    if (!gp.recording) {
      recordStartRef.current = null;
      limitWarnedRef.current = false;
      limitStoppedRef.current = false;
      setLimitWarnOpen(false);
      setLimitRemaining(null);
      return undefined;
    }
    if (recordStartRef.current == null) recordStartRef.current = Date.now();
    const tick = () => {
      const start = recordStartRef.current;
      if (start == null) return;
      const elapsed = Math.floor((Date.now() - start) / 1000);
      setLimitRemaining(Math.max(0, maxClipSec - elapsed));
      if (!limitWarnedRef.current && elapsed >= maxClipSec - WARN_LEAD_SEC && elapsed < maxClipSec) {
        limitWarnedRef.current = true;
        setLimitWarnOpen(true);
        try { playRecordLimitWarningCue(); } catch (_) {}
        try { speak(`Heads up. This GoPro clip will stop in ${WARN_LEAD_SEC} seconds.`); } catch (_) {}
      }
      if (!limitStoppedRef.current && elapsed >= maxClipSec) {
        limitStoppedRef.current = true;
        setLimitWarnOpen(false);
        const stop = toggleRecordRef.current;
        if (stop) { try { stop(); } catch (_) {} }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [gp.recording, maxClipSec]);

  const [wifiJoining, setWifiJoining] = useState(false);
  const [wifiPhase, setWifiPhase] = useState(null); // "creds" | "ap" | "join"
  const handleConnectWifi = wrap(async () => {
    setWifiJoining(true);
    try {
      setWifiPhase("creds");
      // Ensure creds are loaded and AP is enabled before phone-side join.
      // gp.connectWifi() does this internally but we surface phase text here.
      try { await gp.readWifiCredentials?.(); } catch (_) {}
      setWifiPhase("ap");
      try { await gp.enableAp?.(); } catch (_) {}
      setWifiPhase("join");
      await gp.connectWifi();
    } finally {
      setWifiJoining(false);
      setWifiPhase(null);
    }
  });
  const handleDisconnectWifi = wrap(async () => {
    await gp.disconnectWifi();
    setClipList([]);
  });

  const handleListMedia = wrap(async () => {
    // Refreshing the clip list is also the user's "back to clips" action after
    // a download/upload — clear any lingering preview/upload state so the list
    // is visible (the list is hidden while `downloaded` is truthy).
    setDownloaded(null);
    setPreviewSrc(null);
    setUploadPct(null);
    setUploadDone(null);
    setUploadCertId(null);

    // The HTTP listing endpoint (10.5.5.9:8080) is only reachable when the
    // phone is joined to the GoPro Wi-Fi AP. After a Confirm & Certify upload
    // we intentionally release that binding so Firebase can route over the
    // normal network — so we may need to rejoin before refreshing.
    if (gp.connected && !gp.wifi.connected) {
      setWifiJoining(true);
      setWifiPhase("join");
      try {
        await gp.connectWifi();
      } finally {
        setWifiJoining(false);
        setWifiPhase(null);
      }
    }
    const list = await gp.listMedia();
    setClipList(list);
    enrichClipsWithCatch(list);
  });

  // Open the styled confirm modal; the actual delete runs from the modal's
  // "Delete" button via performDelete().
  const handleDelete = (clip) => setConfirmDelete(clip);

  const performDelete = wrap(async (clip) => {
    // Make sure the phone is on the GoPro Wi-Fi AP before issuing the HTTP
    // delete; otherwise the request lands on the wrong network and fails.
    if (gp.connected && !gp.wifi.connected) {
      setWifiJoining(true);
      setWifiPhase("join");
      try {
        await gp.connectWifi();
      } finally {
        setWifiJoining(false);
        setWifiPhase(null);
      }
    }
    await gp.deleteClip(clip);
    setClipList((prev) => prev.filter((c) => !(c.folder === clip.folder && c.name === clip.name)));
  });

  const handleDownload = wrap(async (clip) => {
    setDownloadingClip(true);
    try {
    setDownloaded(null);
    setPreviewSrc(null);
    setUploadPct(null);
    setUploadDone(null);
    const res = await gp.downloadClip(clip);
    const localUri = res?.uri || (res?.path ? `file://${res.path}` : null);
    let webSrc = null;
    if (localUri) {
      try {
        webSrc = Capacitor.convertFileSrc(localUri);
      } catch (_) { webSrc = localUri; }
    }
    setDownloaded({ ...res, name: clip.name, key: `${clip.folder}/${clip.name}` });
    setPreviewSrc(webSrc);
    setStatusOpen(false);

    // Hand off to the Confirm & Certify UI (VideoScreen) if the host wired it up.
    if (onCertify) {
      // Tournament integrity: don't let a GoPro clip proceed to certify/upload
      // outside the active tournament window.
      assertTournamentUploadAllowed();
      try {
        // Release the GoPro AP binding FIRST. The clip is already on local disk,
        // so reading it needs no network — and while the process is bound to the
        // internet-less GoPro AP the Capacitor localhost file bridge (used by
        // convertFileSrc) is unreachable, so building the blob would fail and the
        // review/upload hand-off would never happen.
        const wasOnGoProWifi = gp.wifi.connected;
        if (wasOnGoProWifi) {
          try { await gp.disconnectWifi(); } catch (_) {}
          await new Promise((r) => setTimeout(r, 800));
        }

        // Read the downloaded file into a blob, trying the streamable bridge URL
        // first, then the raw file:// path.
        let blob = null;
        let lastErr = null;
        for (const url of [webSrc, localUri, res?.path ? `file://${res.path}` : null].filter(Boolean)) {
          try {
            const resp = await fetch(url);
            blob = await resp.blob();
            if (blob) break;
          } catch (e) { lastErr = e; }
        }
        if (!blob) throw lastErr || new Error("Could not read the downloaded clip");

        onCertify({
          blob,
          fileName: clip.name,
          mimeType: "video/mp4",
          source: "gopro",
          clipKey: `${clip.folder}/${clip.name}`,
          // Original camera filename, preserved even when the clip is trimmed
          // (trimmed uploads change originalName). Lets the clip list re-link the
          // catch summary by camera name.
          goproClipName: clip.name,
          // On-disk source for the native trimmer (VideoTrim). res.path is an
          // absolute filesystem path; localUri is its file:// form.
          sourcePath: res?.path || null,
          sourceUri: localUri || null,
          // If the user logged the catch right after recording, carry it through
          // so VideoScreen certifies against the real catch (no post-upload Log
          // Catch step). Consumed/cleared by the host on hand-off.
          pendingCatch: pendingCatch || null,
          // Direct, streamable file URL for the in-app preview. Playing from this
          // (served by the Capacitor file bridge) streams from disk, unlike an
          // object URL built from the whole blob which can exceed WebView memory
          // for large GoPro clips and render an unplayable gray frame.
          previewUrl: webSrc || localUri || (res?.path ? `file://${res.path}` : null),
          // Mark the clip as uploaded ONLY when the Confirm & Certify screen
          // reports a genuine, completed upload — not optimistically at hand-off.
          // Otherwise a clip the user reviews but backs out of would be wrongly
          // struck through as "uploaded".
          onUploaded: () => {
            try { markClipUploaded(`${clip.folder}/${clip.name}`); } catch (_) {}
          },
          // Auto-rejoin the GoPro AP when the user returns from Confirm & Certify
          // so they don't have to tap "Join GoPro Wi-Fi" again.
          onComplete: async () => {
            if (!wasOnGoProWifi) return;
            if (!gp.connected) return;
            try {
              await gp.connectWifi();
              // Give the AP a moment to settle, then refresh the clip list.
              await new Promise((r) => setTimeout(r, 600));
              try { await handleListMedia(); } catch (refreshErr) {
                // eslint-disable-next-line no-console
                console.warn("[GoPro] auto-refresh after rejoin failed:", refreshErr?.message || refreshErr);
              }
            } catch (rejoinErr) {
              // eslint-disable-next-line no-console
              console.warn("[GoPro] auto-rejoin after certify failed:", rejoinErr?.message || rejoinErr);
            }
          },
        });
      } catch (handoffErr) {
        // eslint-disable-next-line no-console
        console.error("[GoPro] Certify handoff failed:", handoffErr);
        throw handoffErr;
      }
    }
    } finally {
      setDownloadingClip(false);
    }
  });

  const handleUpload = wrap(async () => {
    if (!downloaded?.path && !downloaded?.uri) throw new Error("No clip downloaded yet");
    // Tournament integrity: block uploads outside the active tournament window.
    assertTournamentUploadAllowed();
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error("Not signed in");

    // Read local file as a Blob via fetch(file://...) which Capacitor WebView supports.
    // IMPORTANT: do this BEFORE releasing the GoPro Wi-Fi binding, in case the WebView
    // resolves file:// through the bound network context.
    const sourceUrl = previewSrc || downloaded.uri || `file://${downloaded.path}`;
    const resp = await fetch(sourceUrl);
    const blob = await resp.blob();

    // Remember if we were on the GoPro Wi-Fi so we can rejoin after upload.
    const wasOnGoProWifi = gp.wifi.connected;

    // Release the GoPro AP binding so Firebase can route over cellular / regular Wi-Fi.
    // The GoPro AP has no internet, so any upload while bound will stall at 0%.
    if (wasOnGoProWifi) {
      try { await gp.disconnectWifi(); } catch (_) {}
      // Give the OS a moment to fall back to the default network.
      await new Promise((r) => setTimeout(r, 800));
    }

    const fileName = downloaded.name || `gopro_${Date.now()}.mp4`;
    const certId = genCertId();
    setUploadPct(0);
    let fileHash = null;
    try { fileHash = await sha256Hex(blob); } catch (_) {}

    const useR2 = isR2Backend();
    let path;
    let ref = null;
    if (useR2) {
      // Cloudflare R2: multipart presigned upload for large GoPro clips. The
      // backend derives the key videos/{uid}/gopro/{certId}.mp4.
      const { key } = await r2UploadVideo({
        certId,
        blob,
        contentType: "video/mp4",
        source: "gopro",
        onProgress: (loaded, total) => {
          if (total) setUploadPct(Math.round((loaded / total) * 100));
        },
      });
      path = key;
    } else {
      path = `videos/${userId}/gopro/${certId}_${fileName}`;
      ref = storageRef(storage, path);
      const task = uploadBytesResumable(ref, blob, {
        contentType: "video/mp4",
        customMetadata: {
          source: "gopro",
          certId,
          originalName: fileName,
          timestamp: new Date().toISOString(),
          ...(fileHash ? { hash: fileHash } : {}),
        },
      });
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => setUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });
    }
    // Best-effort playback URL; must not gate the metadata writes below. The
    // durable identifier is `path` (storagePath) — readers mint a presigned GET
    // on demand (R2) or hold a durable URL (Firebase).
    let url = "";
    try {
      url = (useR2 ? await r2GetPlaybackUrl(path) : await getDownloadURL(ref)) || "";
    } catch (urlErr) {
      console.warn("[GoPro] failed to mint playback URL (non-fatal; readers resolve from storagePath):", urlErr);
    }
    // Phase 7: on R2 the presigned GET is short-lived and must NOT be persisted
    // as a durable field. Readers re-mint from `storagePath` (here `path`).
    const durableUrl = useR2 ? "" : url;

    // Write certificate + video docs so the GoPro upload follows the same
    // post-record flow as in-app live recording. CatchScreen will backfill
    // catchRef once the user logs the catch details.
    const gps =
      typeof location?.lat === "number" && typeof location?.lng === "number"
        ? { lat: location.lat, lng: location.lng }
        : null;
    const placeholderCatch = { species: "—", size: "—", weight: "—", lure: "—" };
    try {
      await authedWrite(() => setDoc(doc(db, "certificates", certId), {
        certId,
        createdAt: serverTimestamp(),
        timestamp: formatTimestamp(),
        source: "gopro",
        catch: placeholderCatch,
        catchRef: null,
        gps,
        hash: fileHash,
        storagePath: path,
        downloadUrl: durableUrl,
        originalName: fileName,
      }));
      await authedWrite(() => setDoc(
        doc(db, "videos", certId),
        {
          certId,
          createdAt: serverTimestamp(),
          userId,
          source: "gopro",
          role: userProfile?.role || "",
          tournamentId: userProfile?.tournamentId || "",
          tournamentName: userProfile?.tournamentName || "",
          teamId: userProfile?.teamId || "",
          teamName: userProfile?.teamName || "",
          catch: placeholderCatch,
          gps,
          storagePath: path,
          downloadUrl: durableUrl,
          certificateRef: certId,
          catchRef: null,
          originalName: fileName,
        },
        { merge: true }
      ));
      try {
        await sendDirectorCertifiedVideoEvent({
          certId,
          tournamentId: userProfile?.tournamentId || "",
          tournamentName: userProfile?.tournamentName || "",
          teamId: userProfile?.teamId || "",
          teamName: userProfile?.teamName || "",
          userId,
          catchData: placeholderCatch,
          downloadUrl: url,
          source: "gopro-upload",
        });
      } catch (_) {}
    } catch (firestoreErr) {
      // Non-fatal — the storage upload succeeded; user can still log catch later.
      // eslint-disable-next-line no-console
      console.error("[GoPro] Firestore cert/video write failed (non-fatal):", firestoreErr);
    }

    setUploadCertId(certId);
    setUploadDone(url);
    if (downloaded?.key) markClipUploaded(downloaded.key);

    // After Firebase upload completes, rejoin the GoPro AP in the background so the
    // user can immediately pull the next clip without re-tapping "Join GoPro Wi-Fi".
    // Best-effort — don't fail the upload if rejoin fails.
    if (wasOnGoProWifi && gp.connected) {
      try {
        await gp.connectWifi();
      } catch (rejoinErr) {
        // eslint-disable-next-line no-console
        console.warn("[GoPro] auto-rejoin to AP failed:", rejoinErr?.message || rejoinErr);
      }
    }
  });

  // ----- styles -----
  const wrapStyle = {
    position: "absolute",
    inset: 0,
    background: C.deepWater,
    color: C.white,
    padding: "16px 14px 120px",
    boxSizing: "border-box",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  };
  const card = {
    background: C.cardBg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  };
  const btn = (variant = "primary", disabled = false) => ({
    background:
      variant === "danger" ? `linear-gradient(135deg, ${C.red}, #7a1c1c)` :
      variant === "ghost" ? "transparent" :
      variant === "warn" ? C.gold :
      `linear-gradient(135deg, ${C.seafoam}, ${C.teal})`,
    color: variant === "ghost" ? C.muted : "#fff",
    border: variant === "ghost" ? `1px solid ${C.border}` : "none",
    borderRadius: 12,
    padding: "13px 18px",
    fontSize: 13,
    fontFamily: "Georgia, serif",
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    width: "100%",
  });
  const dot = (color) => ({
    display: "inline-block", width: 8, height: 8, borderRadius: "50%",
    background: color, marginRight: 6, verticalAlign: "middle",
  });

  const dlPct = useMemo(() => {
    const p = gp.downloadProgress;
    if (!p?.totalBytes) return null;
    return Math.min(100, Math.round((p.bytesRead / p.totalBytes) * 100));
  }, [gp.downloadProgress]);

  // Voice commands (Phase 2c). Add discard, status, quietMode.
  const [quietUntil, setQuietUntil] = useState(null);
  const [quietTimer, setQuietTimer] = useState(null);
  const manualResume = () => {
    setQuietUntil(null);
    if (quietTimer) clearTimeout(quietTimer);
    voice.start?.();
  };
  const voice = useVoiceCommands({
    onStartRecording: () => { if (gp.connected) gp.startRecording(); },
    onStopRecording:  () => { if (gp.connected) gp.stopRecording(); },
    onUpload:         () => { if (downloaded?.path || downloaded?.uri) handleUpload(); },
    onDiscard:        () => { setDownloaded(null); setPreviewSrc(null); setUploadPct(null); setUploadDone(null); setUploadCertId(null); },
    onStatus: async () => {
      const pct = gp.status.batteryPct != null ? `${gp.status.batteryPct}% battery` : "battery unknown";
      const sd = gp.status.sdRemainingBytes != null ? `${fmtBytes(gp.status.sdRemainingBytes)} free` : "SD unknown";
      const mins = gp.status.sdRemainingBytes && gp.recording ? `about ${Math.floor(gp.status.sdRemainingBytes / (1024*1024*60))} min left` : "minutes left unknown";
      await speak(`GoPro status: ${pct}, ${sd}, ${mins}.`);
    },
    onQuietMode: () => {
      setQuietUntil(Date.now() + 5 * 60 * 1000);
      voice.stop?.();
      if (quietTimer) clearTimeout(quietTimer);
      const t = setTimeout(() => {
        setQuietUntil(null);
        voice.start?.();
      }, 5 * 60 * 1000);
      setQuietTimer(t);
    },
  });
  // Show "Listening" banner with waveform when actively listening for commands
  const showListeningBanner = voice.enabled && voice.state === "listening_command" && !quietUntil;

  return (
    <div style={wrapStyle}>
      {showListeningBanner && (
        <div style={{
          position: "sticky", top: 0, zIndex: 1000,
          margin: "-16px -14px 12px",
          background: C.midnight, color: C.accent, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "10px 0"
        }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Listening…</span>
          <VoiceWave active />
        </div>
      )}
      {/* Manual mic resume tap target for quiet mode */}
      {quietUntil && (
        <div style={{ margin: "18px 0", textAlign: "center" }}>
          <button onClick={manualResume} style={btn("primary")}>Resume Voice Commands</button>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Voice will auto-resume in {Math.max(0, Math.ceil((quietUntil - Date.now())/1000))} seconds</div>
        </div>
      )}
      {sessionId && (
        <div
          style={{
            background: "#160808",
            padding: "13px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
            borderBottom: "1px solid #3a1212",
            margin: "-16px -14px 12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PulseDot />
            <span style={{ fontSize: 11, color: C.red, fontWeight: 700, letterSpacing: 2 }}>SESSION LOGGING IN PROGRESS</span>
          </div>
          <div style={{ marginLeft: 20 }}>
            <Badge color={C.red}>LIVE</Badge>
          </div>
        </div>
      )}
      <div style={{ marginBottom: 4, paddingRight: 44 }}>
        <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3 }}>CAMERA</div>
        <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>GoPro Integration</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, paddingRight: 44 }}>
        <img
          src="/assets/gopro-logo-transparent 3.png"
          alt="GoPro"
          style={{ height: 28, width: "auto", display: "block", border: `1px solid ${C.border}`, borderRadius: 8, padding: 2, marginLeft: "10%" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {voice.supported && (
            <button
              onClick={voice.toggle}
              type="button"
              title={voice.enabled ? `Voice: ${voice.state.replace("_", " ")}` : "Enable voice commands"}
              aria-label={voice.enabled ? "Disable voice commands" : "Enable voice commands"}
              style={{
                height: 34,
                padding: "0 10px",
                borderRadius: 10,
                border: `1px solid ${voice.enabled ? C.seafoam : `${C.muted}66`}`,
                background: voice.enabled ? `${C.seafoam}22` : C.cardBg,
                color: voice.enabled ? C.seafoam : C.muted,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 14, lineHeight: 1 }}>
                {voice.enabled ? (voice.state === "listening_command" ? "🎙️" : "🎤") : "🔇"}
              </span>
              <span style={{ letterSpacing: 1 }}>
                {voice.enabled
                  ? (voice.state === "listening_command" ? "LISTENING…" : "HEY NINER")
                  : "VOICE OFF"}
              </span>
            </button>
          )}
        </div>
      </div>
      {voice.supported && voice.enabled && voice.lastEvent?.type === "command" && voice.lastEvent.data?.transcript && (
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 8, paddingRight: 44 }}>
          heard: "{voice.lastEvent.data.transcript}" → {voice.lastEvent.data.intent}
        </div>
      )}
      {voice.supported && voice.error && (
        <div style={{ fontSize: 10, color: C.red, marginBottom: 8, paddingRight: 44 }}>
          voice: {voice.error}
        </div>
      )}

      {(() => {
        const reason = tournamentUploadBlockReason();
        if (!reason) return null;
        return (
          <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.warning}66`, background: `${C.warning}1a`, color: C.white, fontSize: 11, lineHeight: 1.4 }}>
            <span style={{ fontWeight: 800, color: C.warning, letterSpacing: 1, fontSize: 9, textTransform: "uppercase", marginRight: 6 }}>Uploads closed</span>
            {reason}
          </div>
        );
      })()}

      {/* Status + BLE state (collapsible) */}
      <div style={{ marginBottom: 12 }}>
        <CollapsibleSection
          open={statusOpen}
          onToggle={() => setStatusOpen((v) => !v)}
          headerTitle={statusOpen ? "Hide status" : "Show status"}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>STATUS</span>}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              {!isNative ? (
                <><span style={dot(C.warning)} /><span style={{ color: C.muted }}>Native build required</span></>
              ) : gp.initializing ? (
                <><span style={dot(C.muted)} /><span style={{ color: C.muted }}>Initializing…</span></>
              ) : !gp.available ? (
                <><span style={dot(C.red)} /><span style={{ color: C.muted }}>BT unavailable</span></>
              ) : gp.connected ? (
                <>
                  <span style={dot(C.accent)} />
                  <span>{gp.connected.name}</span>
                  {gp.recording && <span style={{ color: C.red, fontWeight: 700 }}>● REC</span>}
                  {gp.wifi.connected && <span style={{ color: C.muted, fontSize: 11 }}>Wi-Fi</span>}
                </>
              ) : gp.scanning ? (
                <><span style={dot(C.gold)} /><span style={{ color: C.muted }}>Scanning…</span></>
              ) : (
                <><span style={dot(C.muted)} /><span style={{ color: C.muted }}>Idle</span></>
              )}
            </div>
            {gp.connected && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 12, fontSize: 12 }}>
                <div><span style={{ color: C.muted }}>Battery: </span>{gp.status.batteryPct ?? "—"}%</div>
                <div><span style={{ color: C.muted }}>Encoding: </span>{gp.status.encoding == null ? "—" : gp.status.encoding ? "Yes" : "No"}</div>
                <div><span style={{ color: C.muted }}>SD free: </span>{fmtBytes(gp.status.sdRemainingBytes)}</div>
                <div><span style={{ color: C.muted }}>Videos: </span>{gp.status.videoCount ?? "—"}</div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <span style={{ color: C.muted }}>Wi-Fi: </span>
                  {gp.wifi.connected ? `Connected (${gp.wifi.ssid})` : "Not joined"}
                </div>
              </div>
            )}
            {(gp.error || actionError) && (
              <div style={{ fontSize: 11, color: C.warning, marginTop: 8, lineHeight: 1.4 }}>
                {actionError || gp.error?.message || String(gp.error)}
              </div>
            )}
        </CollapsibleSection>
      </div>

      {/* Pair / Scan */}
      {!gp.connected && (
        <>
          <div style={card}>
            <button
              onClick={handleScan}
              disabled={!isNative || busy || gp.scanning}
              style={btn("primary", !isNative || busy || gp.scanning)}
            >
              {gp.scanning ? "Scanning…" : "Scan for GoPro"}
            </button>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
              Camera: Connections → Connect Device → GoPro Quik App.
            </div>
          </div>
          {gp.devices.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Discovered devices</div>
              {gp.devices.map((d) => {
                const isConnecting = connectingId === d.deviceId;
                const isOtherBusy = busy && !isConnecting;
                return (
                  <button
                    key={d.deviceId}
                    onClick={() => handleConnect(d)}
                    disabled={busy}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      width: "100%",
                      background: isConnecting ? C.midnight : "transparent",
                      border: `1px solid ${isConnecting ? C.accent : C.border}`,
                      color: C.white,
                      borderRadius: 10, padding: "10px 12px", marginBottom: 8,
                      cursor: busy ? "default" : "pointer",
                      opacity: isOtherBusy ? 0.5 : 1,
                      textAlign: "left",
                    }}
                  >
                    <span>
                      <div style={{ fontWeight: 600 }}>{d.name}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>{d.deviceId}</div>
                    </span>
                    {isConnecting ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                          style={{
                            width: 14, height: 14, borderRadius: "50%",
                            border: `2px solid ${C.border}`,
                            borderTopColor: C.accent,
                            display: "inline-block",
                            animation: "the99-spin 0.8s linear infinite",
                          }}
                        />
                        <span style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>Connecting to GoPro…</span>
                      </span>
                    ) : typeof d.rssi === "number" ? (
                      <span style={{ fontSize: 10, color: C.muted }}>{d.rssi} dBm</span>
                    ) : null}
                  </button>
                );
              })}
              <style>{`@keyframes the99-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </>
      )}

      {/* Connected: record + Wi-Fi + clips */}
      {gp.connected && (
        <>
          <div style={card}>
            <button
              onClick={handleToggleRecord}
              disabled={busy}
              style={{ ...btn(gp.recording ? "danger" : "primary", busy), fontSize: 16, padding: "18px 14px" }}
            >
              {gp.recording ? "■ Stop Recording" : "● Start Recording"}
            </button>
            {gp.recording && limitRemaining != null && (
              <div style={{ marginTop: 8, fontSize: 11, color: limitRemaining <= WARN_LEAD_SEC ? C.gold : C.muted, textAlign: "center" }}>
                Auto-stops in {Math.floor(limitRemaining / 60)}:{String(limitRemaining % 60).padStart(2, "0")} ({maxClipSec / 60} min limit)
              </div>
            )}
          </div>

          {/* Camera video resolution + frame rate (Open GoPro settings over BLE) */}
          <div style={{ marginBottom: 12 }}>
            <CollapsibleSection
              open={settingsOpen}
              onToggle={() => setSettingsOpen((v) => !v)}
              headerTitle={settingsOpen ? "Hide camera settings" : "Show camera settings"}
              title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2 }}>CAMERA SETTINGS</span>}
              headerRight={(() => {
                const r = RESOLUTION_OPTIONS.find((o) => o.value === selRes)?.label;
                const f = FPS_OPTIONS.find((o) => o.value === selFps)?.label;
                const parts = [r, f ? `${f}fps` : null].filter(Boolean);
                return parts.length ? (
                  <span style={{ fontSize: 11, color: C.sand, marginRight: 4 }}>{parts.join(" · ")}</span>
                ) : null;
              })()}
            >
              {gp.recording && (
                <div style={{ fontSize: 10, color: C.gold, marginBottom: 8 }}>Stop recording to change</div>
              )}
              {[
                { key: "RESOLUTION", options: RESOLUTION_OPTIONS, current: selRes, onPick: (v) => { setSelRes(v); wrap(() => gp.setVideoResolution(v))(); } },
                { key: "FRAME RATE (FPS)", options: FPS_OPTIONS, current: selFps, onPick: (v) => { setSelFps(v); wrap(() => gp.setVideoFps(v))(); } },
              ].map((row, ri) => (
                <div key={row.key} style={{ marginTop: ri ? 10 : 0 }}>
                  <div style={{ fontSize: 9, letterSpacing: 1.5, color: C.muted, marginBottom: 6 }}>{row.key}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {row.options.map((o) => {
                      const active = row.current === o.value;
                      const disabled = gp.recording;
                      return (
                        <button
                          key={o.value}
                          onClick={() => row.onPick(o.value)}
                          disabled={disabled}
                          style={{
                            flex: 1,
                            padding: "9px 0",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: "Georgia, serif",
                            cursor: disabled ? "not-allowed" : "pointer",
                            background: active ? `linear-gradient(135deg, ${C.accent}, ${C.teal})` : "transparent",
                            color: active ? "#fff" : C.sand,
                            border: `1px solid ${active ? C.accent : C.border}`,
                            opacity: disabled && !active ? 0.5 : 1,
                          }}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
                The camera may adjust incompatible resolution/frame-rate combinations.
              </div>
            </CollapsibleSection>
          </div>

          {pendingCatch && (
            <div
              style={{
                ...card,
                display: "flex",
                alignItems: "center",
                gap: 10,
                borderColor: `${C.accent}66`,
              }}
            >
              <span style={{ fontSize: 18 }}>✓</span>
              <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
                <div style={{ fontWeight: 700, color: C.accent }}>Catch logged</div>
                <div style={{ color: C.muted, fontSize: 12 }}>
                  {[pendingCatch.catchData?.species, pendingCatch.catchData?.size, pendingCatch.catchData?.weight]
                    .filter((v) => v && v !== "—")
                    .join(" · ") || "Details saved"}{" "}
                  — will attach to the next clip you certify.
                </div>
              </div>
              {onClearPendingCatch && (
                <button
                  onClick={onClearPendingCatch}
                  disabled={busy}
                  style={{
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 11,
                    cursor: busy ? "not-allowed" : "pointer",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          <div style={card}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Wi-Fi (for clip transfer)</div>
            {!gp.wifi.connected ? (
              <>
                <button onClick={handleConnectWifi} disabled={busy} style={btn("primary", busy)}>
                  {wifiJoining ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          width: 14, height: 14, borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.35)",
                          borderTopColor: "#fff",
                          display: "inline-block",
                          animation: "the99-spin 0.8s linear infinite",
                        }}
                      />
                      {wifiPhase === "creds" ? "Reading GoPro credentials…"
                        : wifiPhase === "ap" ? "Enabling GoPro Wi-Fi AP…"
                        : wifiPhase === "join" ? "Connecting to GoPro Wi-Fi…"
                        : "Connecting to GoPro Wi-Fi…"}
                    </span>
                  ) : "Join GoPro Wi-Fi"}
                </button>
                <style>{`@keyframes the99-spin { to { transform: rotate(360deg); } }`}</style>
              </>
            ) : (
              <>
                <button onClick={handleListMedia} disabled={busy} style={btn("primary", busy)}>
                  {clipList.length ? "Refresh Clip List" : "List Clips"}
                </button>
                <div style={{ height: 8 }} />
                <button onClick={handleDisconnectWifi} disabled={busy} style={btn("ghost", busy)}>
                  Disconnect Wi-Fi
                </button>
              </>
            )}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
              Joining the GoPro AP routes only this app's network traffic to the camera. Cellular stays available for everything else.
            </div>
          </div>

          {/* Download progress — rendered ABOVE the clip list so it stays
              visible even when many clips fill the scrollable list below. */}
          {downloadingClip && gp.downloadProgress && !downloaded && (
            <div style={{ ...card, border: `1px solid ${C.seafoam}`, boxShadow: `0 0 0 1px ${C.seafoam}22` }}>
              <div style={{ fontSize: 12, color: C.white, fontWeight: 700, marginBottom: 6 }}>
                Downloading clip from GoPro… {dlPct ?? 0}%
              </div>
              <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${dlPct ?? 0}%`, height: "100%", background: C.seafoam, transition: "width 0.2s ease" }} />
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>
                {fmtBytes(gp.downloadProgress.bytesRead)} / {fmtBytes(gp.downloadProgress.totalBytes)}
              </div>
            </div>
          )}

          {clipList.length > 0 && !downloaded && (() => {
            const visible = clipList.slice(0, 24);
            const uploadedCount = clipList.reduce((n, c) => n + (uploadedClips.has(`${c.folder}/${c.name}`) ? 1 : 0), 0);
            return (
              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: C.muted }}>Clips on camera</div>
                  {uploadedCount > 0 && (
                    <div style={{ fontSize: 10, color: C.muted }}>
                      {uploadedCount} already uploaded
                    </div>
                  )}
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden", margin: "0 -4px", padding: "0 4px", WebkitOverflowScrolling: "touch" }}>
                  {visible.map((clip) => {
                    const key = `${clip.folder}/${clip.name}`;
                    const isUploaded = uploadedClips.has(key);
                    const catchInfo = clipCatchInfo[key];
                    const fmtLen = (n) => (typeof n === "number" ? `${n.toFixed(1).replace(/\.0$/, "")}"` : null);
                    const fmtWt = (n) => (typeof n === "number" ? `${n.toFixed(1).replace(/\.0$/, "")} lb` : null);
                    const catchParts = catchInfo
                      ? (Array.isArray(catchInfo.display) && catchInfo.display.length
                          ? catchInfo.display
                          : [catchInfo.species, fmtLen(catchInfo.lengthIn), fmtWt(catchInfo.weightLbs)].filter(Boolean))
                      : [];
                    return (
                      <ClipRowWithEligibility
                        key={key}
                        clip={clip}
                        isUploaded={isUploaded}
                        catchParts={catchParts}
                        busy={busy}
                        handleDownload={handleDownload}
                        handleDelete={handleDelete}
                        fmtBytes={fmtBytes}
                        userId={auth.currentUser?.uid}
                        getThumbnail={gp.getThumbnail}
                        wifiBound={gp.wifi.connected}
                        thumbsEnabled={!downloadingClip && !busy}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {!onCertify && downloaded && previewSrc && (
            <div style={card}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                Preview · {downloaded.name} ({fmtBytes(downloaded.size)})
              </div>
              <video
                src={previewSrc}
                controls
                playsInline
                style={{ width: "100%", maxHeight: 320, background: "#000", borderRadius: 8 }}
              />
              <div style={{ height: 10 }} />
              {uploadDone ? (
                <>
                  <div style={{ fontSize: 12, color: C.accent, marginBottom: 8 }}>
                    ✓ Uploaded
                  </div>
                  {onLogCatch && uploadCertId && (
                    <>
                      <button
                        onClick={() => {
                          const cid = uploadCertId;
                          // Reset local upload state before handing off so the
                          // GoPro screen is clean if the user comes back.
                          setDownloaded(null);
                          setPreviewSrc(null);
                          setUploadPct(null);
                          setUploadDone(null);
                          setUploadCertId(null);
                          onLogCatch(cid);
                        }}
                        style={btn("primary")}
                      >
                        Log Catch Details
                      </button>
                      <div style={{ height: 8 }} />
                    </>
                  )}
                  <button
                    onClick={() => { setDownloaded(null); setPreviewSrc(null); setUploadPct(null); setUploadDone(null); setUploadCertId(null); }}
                    style={btn("ghost")}
                  >
                    Skip for now
                  </button>
                </>
              ) : (
                <>
                  <button onClick={handleUpload} disabled={busy} style={btn("primary", busy)}>
                    {uploadPct == null ? "Upload to The 99" : `Uploading… ${uploadPct}%`}
                  </button>
                  <div style={{ height: 8 }} />
                  <button
                    onClick={() => { setDownloaded(null); setPreviewSrc(null); }}
                    disabled={busy}
                    style={btn("ghost", busy)}
                  >
                    Discard
                  </button>
                </>
              )}
            </div>
          )}

          <div style={card}>
            <button onClick={handleDisconnect} disabled={busy} style={btn("ghost", busy)}>
              Disconnect Bluetooth
            </button>
          </div>
        </>
      )}

      {limitWarnOpen && (
        <div
          onClick={() => setLimitWarnOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 375,
            background: "rgba(4,8,15,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1100,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.cardBg,
              border: `1px solid ${C.gold}66`,
              borderRadius: 14,
              padding: 20,
              maxWidth: 360,
              width: "100%",
              boxShadow: C.menuShadow,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>⏱</span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: C.gold,
                }}
              >
                Recording limit reached
              </div>
            </div>
            <div style={{ color: C.white, fontSize: 15, marginBottom: 6 }}>
              This clip hit the {maxClipSec / 60}-minute limit.
            </div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.4, marginBottom: 16 }}>
              It will stop automatically in{" "}
              <strong style={{ color: C.gold }}>{limitRemaining != null ? limitRemaining : WARN_LEAD_SEC}s</strong>.
              Stop now, or keep recording until the limit.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setLimitWarnOpen(false)}
                style={btn("ghost")}
              >
                Keep recording
              </button>
              <button
                onClick={() => {
                  setLimitWarnOpen(false);
                  if (gp.recording) handleToggleRecord();
                }}
                style={btn("warn")}
              >
                Stop now
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogCatchPrompt && (
        <div
          onClick={() => setShowLogCatchPrompt(false)}
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 375,
            background: "rgba(4,8,15,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              padding: 20,
              maxWidth: 360,
              width: "100%",
              boxShadow: C.menuShadow,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>🎣</span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: C.accent,
                }}
              >
                Log the catch?
              </div>
            </div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.4, marginBottom: 16 }}>
              Recording stopped. Log the catch details now and we'll attach them
              when you certify the clip — no need to log it again after upload.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLogCatchPrompt(false)}
                style={btn("ghost")}
              >
                Not now
              </button>
              <button
                onClick={() => {
                  setShowLogCatchPrompt(false);
                  onLogCatchFirst?.();
                }}
                style={btn("primary")}
              >
                Log catch now
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          onClick={() => { if (!busy) setConfirmDelete(null); }}
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 375,
            background: "rgba(4,8,15,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.cardBg,
              border: `1px solid ${C.border}`,
              borderRadius: 14,
              padding: 20,
              maxWidth: 360,
              width: "100%",
              boxShadow: C.menuShadow,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>🗑</span>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: C.red,
                }}
              >
                Delete clip
              </div>
            </div>
            <div style={{ color: C.white, fontSize: 15, marginBottom: 6, wordBreak: "break-all" }}>
              {confirmDelete.name}
            </div>
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.4, marginBottom: 16 }}>
              This will permanently remove the file from the GoPro SD card. This action cannot be undone.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={busy}
                style={btn("ghost", busy)}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const clip = confirmDelete;
                  setConfirmDelete(null);
                  await performDelete(clip);
                }}
                disabled={busy}
                style={btn("danger", busy)}
              >
                {busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
